<template>
  <UApp :toaster="toaster">
    <!-- 背景用 design 的 --bg(#07080c)，与站内其它布局一致 -->
    <div class="flex flex-col bg-[#07080c] min-h-screen custom-scroll">
      <!-- 顶部 Header + Banner（与 freeTools 一致，保证 header 功能可用） -->
      <div class="sticky top-0 z-9999">
        <TopBanners />
        <FotoHeader />
      </div>

      <!-- 主体：居中正文容器（法律/公司页 prose） -->
      <div class="flex-1 flex flex-col">
        <main class="legal-main">
          <article class="legal-wrap">
            <NuxtPage />
          </article>
        </main>
      </div>

      <!-- 底部（全宽） -->
      <FotoFooter v-if="showFooter" />
      <div v-else class="h-5"></div>
    </div>

    <!-- 认证 / 积分 / 预览 模态框（与 freeTools 一致，保证 header 功能可用） -->
    <SignUpModal
      :is-open="isSignUpModalOpen"
      @close="closeSignUpModal"
      @switch-to-sign-in="switchToSignIn"
    />
    <SignInModal
      :is-open="isSignInModalOpen"
      @close="closeSignInModal"
      @switch-to-sign-up="switchToSignUp"
    />
    <NotEnoughCredits
      v-model:is-open="isCreditsModalOpen"
      :required-credits="requiredCredits"
      :current-balance="currentCredits"
      :show-required-credits="showRequiredCredits"
      :view="creditsModalView"
      @close="closeCreditsModal"
    />
    <GlobalImagePreview
      :is-open="isImagePreviewOpen"
      :src="imagePreviewSrc"
      :compare="imagePreviewCompare"
      @close="closeImagePreview"
    />
  </UApp>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import TopBanners from "@/components/TopBanners.vue";
import defaultCssUrl from "~/assets/styles/new-index.css?url";
import FotoHeader from "@/components/FotoHeaderNew.vue";
import FotoFooter from "@/components/FotoFooterNew.vue";
import SignUpModal from "@/components/loginModal/SignUpModal.vue";
import SignInModal from "@/components/loginModal/SignInModal.vue";
import NotEnoughCredits from "@/components/newModal/NotEnoughCredits.vue";
import GlobalImagePreview from "@/components/newModal/GlobalImagePreview.vue";

const toaster = { position: "top-right" };
const route = useRoute();
const showFooter = computed(() => route.meta.showFooter ?? true);

useHead({
  link: [
    {
      rel: "stylesheet",
      href: defaultCssUrl,
      crossorigin: "anonymous",
      key: "layout-style",
    },
    {
      rel: "preload",
      href: "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6439841372484008",
      as: "script",
      crossorigin: "anonymous",
      key: "adsbygoogle-preload",
    },
  ],
  htmlAttrs: { class: "dark" }, // 确保 SSR 也加上 dark
});

defineOptions({
  name: "company-layout",
});

const store = useStore();
const userInfo = computed(() => store.state.user.userInfo);
const currentCredits = computed(() => userInfo.value?.credits_sum || 0);

// 与 freeTools 相同的模态框逻辑（header 的登录/积分入口依赖）
const {
  isSignInModalOpen,
  isSignUpModalOpen,
  closeSignInModal,
  closeSignUpModal,
  switchToSignIn,
  switchToSignUp,
} = useAuthModals();

const {
  isCreditsModalOpen,
  requiredCredits,
  showRequiredCredits,
  creditsModalView,
  closeCreditsModal,
} = useCreditsModal();

const {
  isImagePreviewOpen,
  imagePreviewSrc,
  imagePreviewCompare,
  closeImagePreview,
} = useImagePreview();
</script>

<style scoped>
/* 暗色法律/公司页 prose 容器，配合站点暗色主题（参考 FotoFooterNew 的 --tx / --brand 配色） */
.legal-main {
  /* 与 footer 一致的色板变量 */
  --tx: #f4f6fa;
  --tx-dim: rgba(244, 246, 250, 0.72);
  --tx-faint: rgba(244, 246, 250, 0.5);
  --line: rgba(255, 255, 255, 0.12);
  --line-strong: rgba(255, 255, 255, 0.18);
  --brand: #8b5cf6;
  --brand-hover: #a884ff;
  --code-bg: rgba(255, 255, 255, 0.06);

  flex: 1;
  width: 100%;
  padding: 56px 24px 96px;
  color: var(--tx-dim);
  font-family: "Public Sans", system-ui, -apple-system, sans-serif;
}

.legal-wrap {
  max-width: 760px;
  margin: 0 auto;
  font-size: 16px;
  line-height: 1.7;
}

/* 标题分级 */
.legal-wrap :deep(h1) {
  margin: 0 0 28px;
  color: var(--tx);
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.legal-wrap :deep(h2) {
  margin: 48px 0 16px;
  padding-top: 8px;
  color: var(--tx);
  font-size: 25px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.005em;
}

.legal-wrap :deep(h3) {
  margin: 32px 0 12px;
  color: var(--tx);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.4;
}

.legal-wrap :deep(h4) {
  margin: 24px 0 10px;
  color: var(--tx);
  font-size: 16px;
  font-weight: 600;
}

/* 正文段落 */
.legal-wrap :deep(p) {
  margin: 0 0 18px;
  line-height: 1.7;
}

/* 链接：品牌色 + hover */
.legal-wrap :deep(a) {
  color: var(--brand);
  text-decoration: none;
  border-bottom: 1px solid rgba(139, 92, 246, 0.35);
  transition: color 0.15s, border-color 0.15s;
}

.legal-wrap :deep(a:hover) {
  color: var(--brand-hover);
  border-bottom-color: var(--brand-hover);
}

/* 列表缩进 */
.legal-wrap :deep(ul),
.legal-wrap :deep(ol) {
  margin: 0 0 18px;
  padding-left: 26px;
  line-height: 1.7;
}

.legal-wrap :deep(li) {
  margin: 0 0 8px;
}

.legal-wrap :deep(li > ul),
.legal-wrap :deep(li > ol) {
  margin: 8px 0 4px;
}

/* 强调 */
.legal-wrap :deep(strong),
.legal-wrap :deep(b) {
  color: var(--tx);
  font-weight: 600;
}

/* 引用块：左边线 */
.legal-wrap :deep(blockquote) {
  margin: 24px 0;
  padding: 4px 0 4px 18px;
  border-left: 3px solid var(--brand);
  color: var(--tx-faint);
  font-style: italic;
}

.legal-wrap :deep(blockquote p:last-child) {
  margin-bottom: 0;
}

/* 分隔线 */
.legal-wrap :deep(hr) {
  margin: 40px 0;
  border: 0;
  border-top: 1px solid var(--line);
}

/* 表格：边框 */
.legal-wrap :deep(table) {
  width: 100%;
  margin: 24px 0;
  border-collapse: collapse;
  font-size: 14.5px;
}

.legal-wrap :deep(th),
.legal-wrap :deep(td) {
  padding: 10px 14px;
  border: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.legal-wrap :deep(th) {
  background: rgba(255, 255, 255, 0.04);
  color: var(--tx);
  font-weight: 600;
}

.legal-wrap :deep(tbody tr:nth-child(even) td) {
  background: rgba(255, 255, 255, 0.02);
}

/* 代码：等宽 */
.legal-wrap :deep(code) {
  padding: 2px 6px;
  border-radius: 5px;
  background: var(--code-bg);
  color: #e9d8ff;
  font-family: "SFMono-Regular", "JetBrains Mono", Menlo, Consolas, monospace;
  font-size: 0.875em;
}

.legal-wrap :deep(pre) {
  margin: 0 0 20px;
  padding: 16px 18px;
  overflow-x: auto;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--code-bg);
}

.legal-wrap :deep(pre code) {
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: var(--tx);
  font-size: 13.5px;
  line-height: 1.6;
}

/* 图片 */
.legal-wrap :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
}

@media (max-width: 640px) {
  .legal-main {
    padding: 36px 18px 72px;
  }
  .legal-wrap {
    font-size: 15.5px;
  }
  .legal-wrap :deep(h1) {
    font-size: 30px;
  }
  .legal-wrap :deep(h2) {
    font-size: 22px;
  }
  .legal-wrap :deep(h3) {
    font-size: 18px;
  }
}
</style>
